/**
 * Friendly French labels for the AI-credit feature/task keys the backend
 * records (the `feature` passed to require_ai_credits / meter_ai_call, which
 * lands on each usage row's `task_type`). Used by the outage modal
 * ({@link CreditGuardHost}) and the per-request usage detail table so the user
 * sees "Génération d'article" instead of "content_pipeline_detail".
 *
 * Also maps each feature to the product SERVICE/area that consumed the credit,
 * so the usage view can group "which service consumed which credit".
 * Unknown keys fall back to a prettified version of the key.
 */

export const FEATURE_LABELS: Record<string, string> = {
  crm_recap: 'Récapitulatif CRM',
  ab_copy: 'Test A/B de texte',
  account_intelligence: 'Intelligence de compte',
  community_ai: 'IA Communauté',
  content_generation: 'Génération de contenu',
  generate_content: 'Génération de contenu',
  content_pipeline_theme: 'Génération de thème',
  content_pipeline_title: 'Génération de titre',
  content_pipeline_description: 'Génération de description',
  content_pipeline_detail: 'Rédaction du contenu',
  content_studio: 'Studio de contenu',
  generate_hashtags: 'Génération de hashtags',
  generate_image_prompts: 'Prompts d’image',
  ghostwriter_generate: 'Ghostwriter — rédaction',
  ghostwriter_rewrite: 'Ghostwriter — réécriture',
  ghostwriter_translate: 'Ghostwriter — traduction',
  improve_content: 'Amélioration de contenu',
  langgraph_execute: 'Exécution d’agent',
  plan_nlu: 'Planification (compréhension)',
  post_review: 'Revue de publication',
  post_review_panel: 'Panel de revue IA',
  prospection_ai_search: 'Recherche IA de prospection',
  prospection_calendar: 'Calendrier de prospection',
  prospection_strategy: 'Stratégie de prospection',
  reply_assistant: 'Assistant de réponse',
  repurpose: 'Repurposing de contenu',
  staff_chat: 'Assistant IA (chat)',
  suggestions: 'Suggestions IA',
  test_feature: 'Test',
  // Actual prod task_types observed in ai_usage_records:
  copilot: 'Copilot',
  persona_task: 'Agent — tâche persona',
  extraction: 'Extraction (mémoire)',
  rag_embeddings: 'Embeddings RAG',
  analytics_vision: 'Analyse visuelle (analytics)',
  studio_generate: 'Studio — génération',
  carousel: 'Carrousel',
  thread: 'Fil / thread',
  generation: 'Génération',
  workflow: 'Workflow',
  sdr: 'SDR autopilot',
  // Generic bucket for otherwise-uninstrumented background/agentic LLM calls
  // (debit_for_usage attributes these instead of writing a NULL task_type).
  agent_llm: 'Appel IA (agent)',
  // RAG / semantic retrieval features.
  semantic_search: 'Recherche sémantique',
  rag_search_embed: 'Recherche RAG (embeddings)',
  // Pre-flight-gated orchestration entry points (L-gate).
  mas_run: 'Équipe IA — exécution',
  mas_plan: 'Équipe IA — planification',
  mas_team_plan: 'Équipe IA — composition',
  staff_team_run: 'Équipe IA — mission',
  crm_conversation: 'Analyse de conversation',
  crm_sdr: 'SDR autopilot',
};

/** Which product area consumed the credit (for the per-service usage rollup). */
export const FEATURE_SERVICE: Record<string, string> = {
  crm_recap: 'CRM',
  account_intelligence: 'Prospection',
  prospection_ai_search: 'Prospection',
  prospection_calendar: 'Prospection',
  prospection_strategy: 'Prospection',
  ab_copy: 'Contenu',
  content_generation: 'Contenu',
  generate_content: 'Contenu',
  content_pipeline_theme: 'Contenu',
  content_pipeline_title: 'Contenu',
  content_pipeline_description: 'Contenu',
  content_pipeline_detail: 'Contenu',
  content_studio: 'Studio',
  generate_hashtags: 'Contenu',
  generate_image_prompts: 'Studio',
  ghostwriter_generate: 'Ghostwriter',
  ghostwriter_rewrite: 'Ghostwriter',
  ghostwriter_translate: 'Ghostwriter',
  improve_content: 'Contenu',
  post_review: 'Contenu',
  post_review_panel: 'Contenu',
  repurpose: 'Contenu',
  community_ai: 'Communauté',
  reply_assistant: 'Boîte de réception',
  suggestions: 'IA',
  staff_chat: 'Assistant IA',
  langgraph_execute: 'Équipe IA',
  plan_nlu: 'Équipe IA',
  copilot: 'Copilot',
  persona_task: 'Équipe IA',
  extraction: 'Mémoire',
  rag_embeddings: 'RAG',
  analytics_vision: 'Analytics',
  studio_generate: 'Studio',
  carousel: 'Studio',
  thread: 'Communauté',
  generation: 'Contenu',
  workflow: 'Workflows',
  sdr: 'Prospection',
  agent_llm: 'IA / agents',
  semantic_search: 'RAG',
  rag_search_embed: 'RAG',
  mas_run: 'Équipe IA',
  mas_plan: 'Équipe IA',
  mas_team_plan: 'Équipe IA',
  staff_team_run: 'Équipe IA',
  crm_conversation: 'CRM',
  crm_sdr: 'Prospection',
};

/** Prettify an unknown key: `content_pipeline_detail` → `Content pipeline detail`. */
function prettify(key: string): string {
  const s = key.replace(/[_.-]+/g, ' ').trim();
  return s ? s.charAt(0).toUpperCase() + s.slice(1) : key;
}

export function featureLabel(feature: string | null | undefined): string {
  if (!feature) return '—';
  return FEATURE_LABELS[feature] ?? prettify(feature);
}

export function featureService(feature: string | null | undefined): string {
  if (!feature) return '—';
  return FEATURE_SERVICE[feature] ?? 'IA';
}
